import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "react-oidc-context"
import { Message } from "@/components/chat/types"
import {
  listSessions,
  createSession,
  getSession,
  updateSession,
  appendMessages,
  deleteSession,
  SessionSummary,
} from "@/services/sessionService"

export function useSessions() {
  const auth = useAuth()
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  // 履歴保存の失敗をユーザーに見せる（黙って握りつぶすと「保存されているつもり」の
  // まま会話が消える — 2026-07-02 全体レビュー P0）。成功で自動クリア。
  const [saveError, setSaveError] = useState<string | null>(null)

  // Refs to avoid stale closures
  const currentSessionIdRef = useRef<string | null>(null)
  const titleSetRef = useRef<Set<string>>(new Set())
  // セッション毎の「サーバに保存済みの件数」。保存は全量 PUT でなく差分追記
  // （appendMessages）で行う（2026-07-02 P2: 2乗コストと 500件上限の解消）
  const savedCountRef = useRef<Map<string, number>>(new Map())
  const nextCursorRef = useRef<string | null>(null)
  const currentProjectIdRef = useRef<string | undefined>(undefined)

  // Keep ref in sync with state
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId
  }, [currentSessionId])

  const getIdToken = useCallback(() => {
    const token = auth.user?.id_token
    if (!token) throw new Error("Authentication required")
    return token
  }, [auth.user?.id_token])

  // Load first page of sessions (GSI returns updatedAt descending, no client sort needed)
  const loadSessions = useCallback(async (projectId?: string) => {
    try {
      setIsLoadingSessions(true)
      currentProjectIdRef.current = projectId
      const idToken = getIdToken()
      const result = await listSessions(idToken, projectId)
      setSessions(result.sessions)
      nextCursorRef.current = result.nextCursor
      setHasMore(result.nextCursor !== null)
    } catch (err) {
      console.error("Failed to load sessions:", err)
    } finally {
      setIsLoadingSessions(false)
    }
  }, [getIdToken])

  // Load next page of sessions (infinite scroll)
  const loadMoreSessions = useCallback(async () => {
    if (!nextCursorRef.current || isLoadingMore) return
    try {
      setIsLoadingMore(true)
      const idToken = getIdToken()
      const result = await listSessions(
        idToken,
        currentProjectIdRef.current,
        nextCursorRef.current
      )
      setSessions((prev) => [...prev, ...result.sessions])
      nextCursorRef.current = result.nextCursor
      setHasMore(result.nextCursor !== null)
    } catch (err) {
      console.error("Failed to load more sessions:", err)
    } finally {
      setIsLoadingMore(false)
    }
  }, [getIdToken, isLoadingMore])

  useEffect(() => {
    if (auth.isAuthenticated) {
      loadSessions()
    }
  }, [auth.isAuthenticated, loadSessions])

  // Select an existing session
  const selectSession = useCallback(
    async (sessionId: string) => {
      setCurrentSessionId(sessionId)
      setMessages([])
      setSaveError(null)
      try {
        const idToken = getIdToken()
        const detail = await getSession(idToken, sessionId)
        setMessages(detail.messages || [])
        savedCountRef.current.set(sessionId, (detail.messages || []).length)
      } catch (err) {
        console.error("Failed to load session:", err)
        setMessages([
          {
            role: "assistant" as const,
            content: `Failed to load session: ${err instanceof Error ? err.message : "Unknown error"}`,
            timestamp: new Date().toISOString(),
          },
        ])
      }
    },
    [getIdToken]
  )

  // Start a new chat (reset to welcome screen, session created on first message)
  const startNewChat = useCallback(() => {
    setCurrentSessionId(null)
    setMessages([])
    setSaveError(null)
  }, [])

  // Create a session in the backend and return the new sessionId
  const createNewSession = useCallback(
    async (title: string, projectId?: string): Promise<string> => {
      const idToken = getIdToken()
      const result = await createSession(idToken, title, projectId)
      const summary: SessionSummary = {
        sessionId: result.sessionId,
        title: result.title,
        projectId: result.projectId,
        createdAt: result.createdAt,
        updatedAt: result.updatedAt,
      }
      setSessions((prev) => [summary, ...prev])
      setCurrentSessionId(result.sessionId)
      savedCountRef.current.set(result.sessionId, 0)
      return result.sessionId
    },
    [getIdToken]
  )

  // Save messages to the backend — uses ref for currentSessionId to avoid stale closures
  const saveMessages = useCallback(
    async (msgs: Message[], sessionId?: string) => {
      const id = sessionId || currentSessionIdRef.current
      if (!id) return
      try {
        const idToken = getIdToken()

        // Auto-title on first user message if we haven't set it yet
        const firstUserMsg = msgs.find((m) => m.role === "user")
        if (firstUserMsg && !titleSetRef.current.has(id)) {
          titleSetRef.current.add(id)
          const autoTitle = firstUserMsg.content.slice(0, 50) + (firstUserMsg.content.length > 50 ? "..." : "")
          await updateSession(idToken, id, { title: autoTitle })
          setSessions((prev) =>
            prev.map((s) =>
              s.sessionId === id
                ? { ...s, title: autoTitle, updatedAt: new Date().toISOString() }
                : s
            )
          )
        }

        // 差分追記: 保存済み件数以降だけ送る。サーバの1回上限（50件）に合わせて
        // チャンク分割（失敗後の再送で差分が溜まっていても詰まらない）。409（サーバが
        // 先行＝別端末で保存済み等）はサーバ件数に再同期して残りだけ送り直す。
        const APPEND_CHUNK = 50
        let saved = savedCountRef.current.get(id) ?? 0
        let resynced = false
        while (msgs.length > saved) {
          const chunk = msgs.slice(saved, saved + APPEND_CHUNK)
          try {
            const result = await appendMessages(idToken, id, saved, chunk)
            saved = result.messageCount ?? saved + chunk.length
          } catch (err) {
            if ((err as { status?: number }).status === 409 && !resynced) {
              resynced = true
              const detail = await getSession(idToken, id)
              saved = (detail.messages || []).length
              continue
            }
            throw err
          } finally {
            savedCountRef.current.set(id, saved)
          }
        }
        setSessions((prev) =>
          prev.map((s) =>
            s.sessionId === id ? { ...s, updatedAt: new Date().toISOString() } : s
          )
        )
        setSaveError(null)
      } catch (err) {
        console.error("Failed to save messages:", err)
        setSaveError(
          "会話の保存に失敗しました。直近のやり取りが履歴に残っていない可能性があります。" +
            "通信状況を確認してもう一度送信してください。"
        )
      }
    },
    [getIdToken]
  )

  // Delete a session
  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      try {
        const idToken = getIdToken()
        await deleteSession(idToken, sessionId)
        titleSetRef.current.delete(sessionId)
        savedCountRef.current.delete(sessionId)
        setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId))
        // If the deleted session was active, reset to welcome screen
        if (sessionId === currentSessionIdRef.current) {
          setCurrentSessionId(null)
          setMessages([])
        }
      } catch (err) {
        console.error("Failed to delete session:", err)
      }
    },
    [getIdToken]
  )

  return {
    sessions,
    currentSessionId,
    messages,
    setMessages,
    isLoadingSessions,
    isLoadingMore,
    hasMore,
    saveError,
    loadSessions,
    loadMoreSessions,
    selectSession,
    startNewChat,
    createNewSession,
    saveMessages,
    deleteSession: handleDeleteSession,
  }
}
